import type {
  ContextualCommentUpdateData,
  DealroomEventType,
  EventTriggeredByType,
  FileData,
  ParticipantCreationSource,
  ParticipantData,
  TaskData,
} from '../../timeline/types/log.types';
import type { CommentStatus } from '../../contextual-commenting/types/comment-status';
import type { UpdateTaskDto } from '../../mutual-action-plan/types/update-task-dto';
import type { MeetingStatus } from '../../meetings/types/meeting';

export enum NotificationType {
  Dealroom = 'dealroom',
}

export type NotificationMetaData = {
  messageId?: string;
  conversationId?: string;
  commentId?: string;
  replyId?: string;
  content?: string;
  isContentAvailable?: boolean;
  editorBlockId?: string;
  fileId?: string;
  actionPlanId?: string;
  milestoneId?: string;
  taskId?: string;
  meetingId?: string;
};

export type NotificationEvent = {
  id: string;
  participantId: string;
  eventName: string;
  createdAt: number;
  type: DealroomEventType;
  isRead: boolean;
  eventTitle?: string;
  metaData?: NotificationMetaData;
};

export interface ListNotificationsArgs {
  type: NotificationType;
  dealroomId: string;
  participantId: string;
  offset?: number;
  limit?: number;
  eventTypes?: DealroomEventType[];
}

export interface ListNotificationsResult {
  notifications: Notification[];
  totalCount: number;
  totalUnreadCount: number;
}

export interface Notification {
  id: string;
  entityId: string;
  createdAt: number;
  viewedAt?: number;
  summaryId?: string;
  data: NotificationData;
}

export interface NotificationData {
  type: NotificationType;
  dealroomId: string;
  participantId: string;
  userId?: string;
  event: DealroomNotificationEvent;
}

export interface VisitedNotificationEvent {
  type: DealroomEventType.Visited;
  participantId: string;
  trackingConsent: boolean;
  isFirstVisit: boolean;
  triggeredBy: EventTriggeredByType;
}

export interface FileUploadedNotificationEvent {
  type: DealroomEventType.FileUploaded;
  triggeredBy: EventTriggeredByType;
  participantId: string;
  fileId: string;
  fileData?: FileData;
  size?: number;
}

export interface ParticipantCreatedNotificationEvent {
  type: DealroomEventType.ParticipantCreated;
  triggeredBy: EventTriggeredByType;
  participantId: string;
  createdParticipantId: string;
  creationSource: ParticipantCreationSource;
  createdParticipantData?: ParticipantData;
}

export interface SystemParticipantCreatedNotificationEvent {
  type: DealroomEventType.ParticipantCreated;
  triggeredBy: EventTriggeredByType;
  createdParticipantId: string;
  creationSource: ParticipantCreationSource;
  createdParticipantData?: ParticipantData;
}

export interface EntityAdminParticipantCreatedNotificationEvent {
  type: DealroomEventType.ParticipantCreated;
  triggeredBy: EventTriggeredByType;
  createdParticipantId: string;
  creationSource: ParticipantCreationSource;
  createdParticipantData?: ParticipantData;
  userId: string;
}

export interface ContextualCommentReplyNotificationEvent {
  type: DealroomEventType.ContextualCommentReplyCreated;
  triggeredBy: EventTriggeredByType;
  participantId: string;
  replyId: string;
  commentId: string;
}

export interface ContextualCommentUpdatedNotificationEvent {
  type: DealroomEventType.ContextualCommentUpdated;
  commentStatus: CommentStatus;
  updatedData: ContextualCommentUpdateData;
  triggeredBy: EventTriggeredByType;
  participantId: string;
  commentId: string;
}

export interface MessageCreatedNotificationEvent {
  type: DealroomEventType.ConversationMessageCreated;
  triggeredBy: EventTriggeredByType;
  participantId: string;
  messageId: string;
  conversationId: string;
  participantIds: string[];
}

export interface TaskCreatedNotificationEvent {
  type: DealroomEventType.TaskCreated;
  taskId: string;
  taskData: TaskData;
  milestoneId: string;
  milestoneTitle?: string;
  milestoneIndex?: number;
  actionPlanId: string;
  actionPlanTitle?: string;
  triggeredBy: EventTriggeredByType;
  participantId: string;
}

export interface TaskUpdatedNotificationEvent {
  type: DealroomEventType.TaskUpdated;
  update: Omit<UpdateTaskDto, 'targetMilestoneId'>;
  taskId: string;
  taskData: TaskData;
  milestoneId: string;
  milestoneTitle?: string;
  milestoneIndex?: number;
  actionPlanId: string;
  actionPlanTitle?: string;
  triggeredBy: EventTriggeredByType;
  participantId: string;
}

export interface ParticipantDeletedNotificationEvent {
  type: DealroomEventType.ParticipantDeleted;
  triggeredBy: EventTriggeredByType;
  participantId: string;
  deletedParticipant: ParticipantData & { participantId: string };
}

export interface EntityAdminParticipantDeletedNotificationEvent {
  type: DealroomEventType.ParticipantDeleted;
  triggeredBy: EventTriggeredByType;
  userId: string;
  deletedParticipant: ParticipantData & { participantId: string };
}

export interface RoomOwnerUpdatedNotificationEvent {
  type: DealroomEventType.RoomOwnerUpdated;
  triggeredBy: EventTriggeredByType;
  oldRoomOwnerUserId: string;
  oldRoomOwnerParticipantId: string;
  newRoomOwnerUserId: string;
  newRoomOwnerParticipantId: string;
  participantId: string;
}

export interface EntityAdminRoomOwnerUpdatedNotificationEvent {
  type: DealroomEventType.RoomOwnerUpdated;
  triggeredBy: EventTriggeredByType;
  userId: string;
  oldRoomOwnerUserId: string;
  oldRoomOwnerParticipantId: string;
  newRoomOwnerUserId: string;
  newRoomOwnerParticipantId: string;
}

export interface MeetingCreatedNotificationEvent {
  type: DealroomEventType.MeetingCreated;
  meetingId: string;
  participantId: string;
  meetingStatus: MeetingStatus;
  title: string;
}

export interface MeetingUpdatedNotificationEvent {
  type: DealroomEventType.MeetingUpdated;
  meetingId: string;
  participantId: string;
  meetingStatus: MeetingStatus;
  title: string;
  updatedData: {
    [K in 'meetingStatus' | 'status']?: MeetingStatus;
  };
}

export type DealroomNotificationEvent =
  | VisitedNotificationEvent
  | FileUploadedNotificationEvent
  | ParticipantCreatedNotificationEvent
  | SystemParticipantCreatedNotificationEvent
  | EntityAdminParticipantCreatedNotificationEvent
  | ContextualCommentReplyNotificationEvent
  | ContextualCommentUpdatedNotificationEvent
  | MessageCreatedNotificationEvent
  | TaskCreatedNotificationEvent
  | TaskUpdatedNotificationEvent
  | ParticipantDeletedNotificationEvent
  | EntityAdminParticipantDeletedNotificationEvent
  | RoomOwnerUpdatedNotificationEvent
  | EntityAdminRoomOwnerUpdatedNotificationEvent
  | MeetingCreatedNotificationEvent
  | MeetingUpdatedNotificationEvent;
